import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  SetStateAction,
} from "react";
import PostYourPropertyCard from "../../propertyList/PostYourPropertyCard";
import HomeLoanCard from "../../propertyList/HomeLoanCard";
import BenefitsOfBuyingCard from "../../propertyList/BenefitsOfBuyingCard";
import { Dropdown } from "../../../../shadcn/components/custom/Dropdown";
import { amountFormatter, Context, getWatermarkedImgixUrl } from "services";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import PrivatePropertyCard from "./PrivatePropertyCard";
import { AuthModalWrapper } from "../../Auth/AuthModal";

interface HeadingSectionProps {
  allPropertiesData: any;
  propertyCount: any;
  isFetchingNextPage: boolean;
  handleClearFilters: any;
}
interface SideBarProps {
  authModalOpen?: boolean;
  setAuthModalOpen: React.Dispatch<SetStateAction<boolean>>;
  isLoggedIn?: boolean;
}

export default function HeadingSection({
  allPropertiesData,
  propertyCount,
  isFetchingNextPage,
  handleClearFilters,
}: HeadingSectionProps) {
  const router = useRouter();
  const shortlistedProperties = useSelector((state: any) => {
    return state.auth?.user?.profile?.meta?.shortlisted_properties;
  });
  const [isShrunk, setIsShrunk] = useState(false);
  const { searchForm }: any = useContext(Context);
  const isLoggedIn = useSelector((state: any) => state.auth.accessToken);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const location = searchForm.value.city.label || "All Locations";

  const dropdownOptions = [
    { label: "All Properties", value: "all_properties" },
    { label: "Price: Low to high", value: "reserve_price_asc" },
    { label: "Price: High to low", value: "reserve_price_desc" },
  ];

  const handleSortingOptions = (data: any) => {
    if (data?.value === "all_properties") {
      handleClearFilters({ type: "all_properties" });
    }
    searchForm.setField("sort_by", data);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsShrunk(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const show = useMemo(() => {
    return allPropertiesData
      .map((property: any, index: number) => {
        const components = [
          <PrivatePropertyCard
            key={`property-${index}`}
            propertyData={property}
            assetPath={
              property?.dump?.property_image
                ? getWatermarkedImgixUrl(
                    `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${property?.dump?.property_image}`,
                  )
                : property?.dump?.asset?.placeholder_image
                ? getWatermarkedImgixUrl(
                    `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${property.dump.asset.placeholder_image}`,
                  )
                : "/newPropertyImage.png"
            }
            imgClasses={"object-cover"}
            showPrice={true}
            price={amountFormatter(
              property?.reserve_price !== 0
                ? property?.reserve_price
                : property?.posted_properties_data?.base_price_proposed_last_rp
                ? property?.posted_properties_data?.base_price_proposed_last_rp
                : property?.posted_properties_raw_data?.propertyCost,
            )}
            priceTag="reserve Price"
            isTagged={property?.interested_count > 0}
            tagNumber={property?.interested_count}
            listingId={property.listing_id}
            location={`${property.dump.location.city_name}, ${property.dump.location.state_name}`}
            propertyInfo={property.description}
            propertyType={`${property.dump.asset.asset_type_name} / ${property.dump.asset.sub_asset_type_name}`}
            carpetArea={
              property?.posted_properties_raw_data?.landSize
                ? property?.posted_properties_raw_data?.landSize
                : property?.posted_properties_raw_data?.builtUpArea
                ? property?.posted_properties_raw_data?.builtUpArea
                : "N/A"
            }
            deadlineDate={
              property.property_date_type === "undated"
                ? "Coming Soon"
                : property.property_date_type === "private_treaty"
                ? "Private Treaty"
                : property?.emd_last_date &&
                  DateTime.fromISO(property?.emd_last_date)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")
            }
            sellerName={property.dump.bank.bank_name}
            sellerIcon={
              property.dump.bank.bank_logo ? (
                <img
                  src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${property.dump.bank.bank_logo}?w=32&h=32&auto=format`}
                />
              ) : undefined
            }
            auctionDate={
              property.property_date_type === "undated"
                ? "Coming Soon"
                : property.property_date_type === "private_treaty"
                ? "Private Treaty"
                : property?.auction_start_date_time &&
                  DateTime.fromISO(property?.auction_start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")
            }
            tag={undefined}
            showTagText={true}
            isVerified={property.ab_verified}
            isLiked={shortlistedProperties?.includes(property.uuid)}
            whatsAppHandler={() => {}}
            sortingHandler={() => {}}
            contactHandler={() => {}}
          />,
        ];
        return components;
      })
      .flat();
  }, [allPropertiesData]);

  return (
    <div
      className={`lg:max-w-[1240px] 2xl:max-w-[1340px] w-full mx-auto ${
        isShrunk ? "mt-4" : "mt-4"
      }`}
    >
      <main className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_258px] lg:gap-x-8">
        <section
          className={`order-1 sticky top-[115px] lg:top-[232px] xl:top-[183px] w-full z-10 ${
            isShrunk ? "col-span-1" : "col-span-2"
          }`}
        >
          <div className="w-full  max-w-[1240px] 2xl:max-w-[1340px] mx-auto flex items-center justify-between bg-white z-10 lg:pb-[10px] lg:pt-2 duration-100 ease-in">
            <div>
              <header
                className={`font-space font-normal leading-normal transition-all duration-300 ease-out transform uppercase ${
                  isShrunk
                    ? "text-xs mobile:text-base sm:text-xl lg:text-base py-4 md:py-0"
                    : "text-[28px] md:text-[48px]"
                } lg:text-center text-brand-textBlack`}
              >
                {isShrunk
                  ? `${
                      searchForm.value.asset_type.label || "All"
                    } properties in ${location} for auction ${
                      propertyCount || 0
                    }`
                  : `${searchForm.value.asset_type.label || "All"} properties`}
              </header>
              {!isShrunk && (
                <div className="text-xs md:text-xl text-brand-borderGrey uppercase duration-300 ease-out">
                  {`in ${location} ${propertyCount || 0}`}
                </div>
              )}
            </div>
            <div className=" duration-300 ease-in-out hidden lg:flex items-center">
              {/* <FunnelSimpleIcon /> */}
              <span className="ml-2 mr-[13px] font-openSans text-sm font-normal leading-4 tracking-wide">
                Sort by:
              </span>
              <div>
                <Dropdown
                  dataSource={() => Promise.resolve(dropdownOptions)}
                  defaultSelected={
                    searchForm.getField("sort_by") !== ""
                      ? searchForm.getField("sort_by")
                      : undefined
                  }
                  onChange={(value: any) => handleSortingOptions(value)}
                  valueKey="value"
                  placeholder="Filter"
                  styles={{
                    outerContainer:
                      "border-[1px] border-brand-borderGrey min-w-[170px] rounded-lg text-sm leading-normal font-openSans text-brand-textBlack px-4",
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        <section
          className={` order-3 w-[258px] hidden lg:block ${
            isShrunk ? "order-2 row-span-10 " : ""
          }`}
        >
          <div className="sticky lg:top-[234px] xl:top-[190px]">
            <div className="overflow-y-scroll">
              <SideBarSection
                authModalOpen={authModalOpen}
                setAuthModalOpen={setAuthModalOpen}
                isLoggedIn={isLoggedIn}
              />
            </div>
          </div>
        </section>

        <section
          className={`flex flex-col justify-center items-center md:items-stretch lg:justify-start gap-8 order-2 w-full ${
            isShrunk ? "order-3" : "pt-4 md:pt-2 lg:pt-0"
          }`}
        >
          {show.length > 0
            ? show
            : !isFetchingNextPage && (
                <div className="min-h-svh">
                  <div className="text-lg font-openSans text-brand-textLightGrey text-center mt-6 capitalize tracking-wider">
                    No property data matches for the selected filters.
                  </div>
                </div>
              )}
          {isFetchingNextPage && (
            <div className="flex justify-center items-center">
              <div className="w-14 h-14 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
            </div>
          )}
        </section>
      </main>
      <AuthModalWrapper
        userLoggedin={isLoggedIn}
        authModelOpen={authModalOpen}
        setAuthModelOpen={setAuthModalOpen}
      />
    </div>
  );
}

const SideBarSection = ({
  authModalOpen,
  setAuthModalOpen,
  isLoggedIn,
}: SideBarProps) => {
  const handlePostYourPropertyClick = () => {
    if (isLoggedIn) window.open(`/post-your-property`, "_blank");
    else setAuthModalOpen(true);
  };
  return (
    <>
      <PostYourPropertyCard
        title="Do you own property and would want to put it up for auction?"
        buttonText="Post Your Property"
        onButtonClick={handlePostYourPropertyClick}
        imageSrc="./addNewProperty.png"
        imageAlt="Property Image"
      />
      <div className="my-6">
        <HomeLoanCard />
      </div>
      <BenefitsOfBuyingCard title="Benefits of buying auctioned property" />
    </>
  );
};
